/////////////////////////////////////////////////////////////////////////////////////////////////////////
// 화일명 : useTokenExpiry.ts
// JWT 세션 만료 감시 훅
//  - 세션쿠키(httpOnly)는 JS로 못 읽으므로(2026-10-17 전환), 로그인/MFA/세션연장 응답이 돌려주는
//    만료시각(tokenExpiresAt, ms epoch - 민감하지 않음)만 localStorage에 들고 카운트다운함.
//  - tokenExpiresAt이 없으면 로그인 페이지로 리다이렉트
//  - 1초마다 남은시간(시:분:초)을 계산해서 반환
//  - 만료 1분 전에 showExtendPrompt를 한 번 true로 올림(연장 여부를 물어볼 시점을 알려줌) -
//    실제 확인창(confirmMsgBox)과 토큰 재발급 API 호출은 이 훅을 쓰는 화면(mainFrame)에서 처리하고,
//    새 만료시각을 extendTokenExpiry()로 넘겨주면 카운트다운이 다시 시작됨.
//  - 만료되면 로그인정보를 정리하고 로그인 페이지로 리다이렉트
/////////////////////////////////////////////////////////////////////////////////////////////////////////
import { useEffect, useRef, useState, useCallback } from 'react';
import type { NavigateFunction } from 'react-router-dom';
import { clearUserInfo } from '../utils/userUtils';
import { useMsgBoxModal } from '../components/CommonModal';

const EXTEND_PROMPT_THRESHOLD_SEC = 60; // 남은시간이 이 값 이하로 떨어지면 연장 여부를 한 번 물어봄

export function useTokenExpiry(navigate: NavigateFunction): {
  tokenExpiry: number | null;
  tokenRemainHMS: string | null;
  showExtendPrompt: boolean;
  dismissExtendPrompt: () => void;
  extendTokenExpiry: (newExpiresAt: number) => void;
} {
  const [tokenExpiry, setTokenExpiry] = useState<number | null>(null);
  const [tokenRemainHMS, setTokenRemainHMS] = useState<string | null>(null);
  const [showExtendPrompt, setShowExtendPrompt] = useState(false);
  const promptShownRef = useRef(false); // 이번 만료 주기에서 이미 물어봤는지(1초마다 재실행 방지)
  const { hideMsgBox } = useMsgBoxModal(); // 만료시 열려있는 연장확인 메시지박스를 같이 닫기 위함

  // 만료시각 확인
  useEffect(() => {
    const stored = Number(localStorage.getItem('tokenExpiresAt'));
    if (!stored) {
      setTokenExpiry(null);
      navigate('/');
      return;
    }
    setTokenExpiry(stored);
  }, [navigate]);

  // 1초마다 남은 시간 갱신 (시:분:초로 변환)
  useEffect(() => {
    if (!tokenExpiry) {
      setTokenRemainHMS(null);
      return;
    }
    const updateRemain = () => {
      const remain = Math.max(0, Math.floor((tokenExpiry - Date.now()) / 1000));
      // 시:분:초로 변환
      const h = Math.floor(remain / 3600);
      const m = Math.floor((remain % 3600) / 60);
      const s = remain % 60;
      const hms = [h, m, s]
        .map(unit => unit.toString().padStart(2, '0'))
        .join(':');
      setTokenRemainHMS(hms);

      // 남은시간이 임계값(1분) 이하로 떨어지면 이번 주기에 한 번만 연장 프롬프트 요청
      if (remain > 0 && remain <= EXTEND_PROMPT_THRESHOLD_SEC && !promptShownRef.current) {
        promptShownRef.current = true;
        setShowExtendPrompt(true);
      }

      // 만료시간이 0 이하가 되면 로그인정보 정리 및 로그인 페이지로 이동
      if (remain <= 0) {
        localStorage.removeItem('tokenExpiresAt');
        clearUserInfo();
        setTokenExpiry(null);
        setTokenRemainHMS(null);
        setShowExtendPrompt(false);
        hideMsgBox(); // 연장확인 등 열려있는 메시지박스가 로그아웃 후에도 남아있지 않도록 닫음
        navigate('/', { replace: true });
      }
    };
    updateRemain();
    const interval = setInterval(updateRemain, 1000);
    return () => clearInterval(interval);
  }, [tokenExpiry, navigate, hideMsgBox]);

  // 취소(또는 확인창 처리 후) - 프롬프트만 닫음. 카운트다운은 그대로 진행되어 자연스럽게 만료됨.
  const dismissExtendPrompt = useCallback(() => {
    setShowExtendPrompt(false);
  }, []);

  // 세션연장 응답의 새 만료시각으로 카운트다운을 다시 시작(최초 발급 때와 동일한 길이) + 프롬프트 닫기
  const extendTokenExpiry = useCallback((newExpiresAt: number) => {
    localStorage.setItem('tokenExpiresAt', String(newExpiresAt));
    promptShownRef.current = false; // 다음 만료 주기에서 다시 물어볼 수 있도록 리셋
    setShowExtendPrompt(false);
    setTokenExpiry(newExpiresAt);
  }, []);

  return { tokenExpiry, tokenRemainHMS, showExtendPrompt, dismissExtendPrompt, extendTokenExpiry };
}
